/* Adapt the installed Simpler Shopify buttons to Prestige without changing
 * their checkout payloads. The SDK still owns button creation and checkout. */
(() => {
  if (window.izySimplerCartConnected) return;
  window.izySimplerCartConnected = true;
  let refreshVersion = 0;
  let scheduled = false;
  const styles = new Map();
  let rootsObserver = null;
  const shapeCss = '/* IZY Prestige button shape */ simpler-button button, simpler-button button:hover { border-radius: 0 !important; box-sizing: border-box; height: var(--izy-simpler-button-height, 2.75rem) !important; }';
  const drawerSelector = '#cart-drawer [data-izy-simpler-cart] simpler-checkout';
  const productSelector = ':is(.shopify-section--main-product, quick-buy-modal) .product-info simpler-checkout[placement="product"]';

  function disconnect(button, binding) {
    binding.renderObserver?.disconnect();
    binding.sizeObserver?.disconnect();
    if (binding.originalParams) button.getCheckoutParams = binding.originalParams;
    if (binding.originalUnavailable) button.isCurrentSelectionUnavailable = binding.originalUnavailable;
    styles.delete(button);
  }

  function styleButton(button, nativeButton, isDrawer) {
    const previous = styles.get(button);
    if (previous?.nativeButton === nativeButton) {
      if (!isDrawer) button.requestUpdate?.();
      return;
    }
    if (previous) disconnect(button, previous);
    const binding = { nativeButton };
    styles.set(button, binding);
    if (!isDrawer && nativeButton?.form) {
      // The SDK queries the first form in the document. Scope product/quickview
      // checkout to this button's form so a different product cannot supply its size.
      const form = nativeButton.form;
      binding.originalParams = button.getCheckoutParams;
      binding.originalUnavailable = button.isCurrentSelectionUnavailable;
      button.isCurrentSelectionUnavailable = () => {
        return !form.isConnected || !form.elements.namedItem('id')?.value ||
          Boolean(form.izySizeChoice && !form.izySizeChoice.ready) || nativeButton.disabled;
      };
      button.getCheckoutParams = () => {
        if (button.isCurrentSelectionUnavailable()) throw new Error('Select an available size before checkout.');
        const params = binding.originalParams.call(button);
        if (params.prd?.length !== 1) throw new Error('Unexpected product checkout payload.');
        return { ...params, prd: [{ ...params.prd[0],
          id: `gid://shopify/ProductVariant/${form.elements.namedItem('id').value}`,
          qty: Number(form.elements.namedItem('quantity')?.value) || 1
        }] };
      };
      button.requestUpdate?.();
    }
    const current = () => button.isConnected && styles.get(button) === binding;
    const matchHeight = () => {
      if (!current() || !nativeButton?.isConnected) return;
      const height = nativeButton.getBoundingClientRect().height;
      if (height > 0) button.style.setProperty('--izy-simpler-button-height', `${height}px`);
    };
    if (nativeButton) {
      binding.sizeObserver = new ResizeObserver(matchHeight);
      binding.sizeObserver.observe(nativeButton);
      matchHeight();
    }
    const applyAppearance = () => {
      if (!current()) return;
      if (isDrawer && button.separator !== 'bottom') button.separator = 'bottom';
      if (!(button.rawCss || '').includes(shapeCss)) {
        button.rawCss = `${button.rawCss || ''}\n${shapeCss}`;
      }
      // Expose narrowly named CSS parts for merchant styling through the
      // component's shadow root. Preserve any parts provided by Simpler.
      const parts = {
        'simpler-button button': 'izy-button',
        '.separator': 'izy-separator',
        '.separator span': 'izy-separator-text',
        '.separator .line': 'izy-separator-line',
        '.payment-methods-with-loyalty': 'izy-payment-icons'
      };
      for (const [selector, part] of Object.entries(parts)) {
        for (const element of button.shadowRoot?.querySelectorAll(selector) || []) {
          if (!element.part.contains(part)) element.part.add(part);
        }
      }
    };
    applyAppearance();
    Promise.resolve(button.updateComplete).then(() => {
      if (!current() || !button.shadowRoot) return;
      binding.renderObserver = new MutationObserver(applyAppearance);
      binding.renderObserver.observe(button.shadowRoot, { childList: true, subtree: true, characterData: true });
      applyAppearance();
    });
  }

  function scanButtons() {
    scheduled = false;
    for (const [button, binding] of styles) {
      if (!button.isConnected || !button.matches(`${drawerSelector}, ${productSelector}`)) disconnect(button, binding);
    }
    for (const button of document.querySelectorAll(drawerSelector)) {
      styleButton(button, document.querySelector('#cart-drawer button[name=checkout]'), true);
    }
    for (const button of document.querySelectorAll(productSelector)) {
      const scope = button.closest('.product-info');
      styleButton(button, scope?.querySelector('buy-buttons button[type=submit]'), false);
    }
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(scanButtons);
  }

  function observeRoots() {
    rootsObserver?.disconnect();
    rootsObserver = new MutationObserver(scheduleScan);
    for (const root of document.querySelectorAll('#cart-drawer, .shopify-section--main-product, quick-buy-modal[open]')) {
      rootsObserver.observe(root, { childList: true, subtree: true });
    }
    scanButtons();
  }

  customElements.whenDefined('simpler-checkout').then(observeRoots);
  document.addEventListener('dialog:before-show', event => {
    if (!event.target.matches('quick-buy-modal') || !rootsObserver) return;
    rootsObserver.observe(event.target, { childList: true, subtree: true });
    scheduleScan();
  }, true);
  document.addEventListener('shopify:section:load', () => {
    if (customElements.get('simpler-checkout')) observeRoots();
  });
  document.addEventListener('shopify:section:unload', scheduleScan);
  document.addEventListener('variant:change', scheduleScan);

  document.addEventListener('cart-drawer:refreshed', async () => {
    const version = ++refreshVersion;
    const slot = document.querySelector('#cart-drawer [data-izy-simpler-cart]');
    if (!slot || !window.simplerCheckoutAppId || !customElements.get('simpler-checkout')) return;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (version !== refreshVersion || !slot.isConnected || !cart.item_count) return;
      document.dispatchEvent(new CustomEvent('simpler.shopifyCartChanged', {
        bubbles: true,
        detail: cart
      }));
    } catch {
      // Keep the existing checkout available if the cart read fails.
    }
  });
})();
